import { gql, type TypedDocumentNode } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Centered } from '@sorare/core/src/atoms/layout/Centered/index.tsx';

import { CompetitionPicker } from '#src/components/pickers/CompetitionPicker/index.tsx';
import type { Mode } from '#src/components/pickers/ModePicker/index.tsx';
import { ModePicker } from '#src/components/pickers/ModePicker/index.tsx';
import { RarityPicker } from '#src/components/pickers/RarityPicker/index.tsx';

import type { ArenaPickers_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

type Props = {
  rarities?: readonly Rarity[];
  selectedRarity: Rarity;
  onSelectRarity: (rarity: Rarity) => void;
  modes?: readonly Mode[];
  selectedMode: Mode;
  onSelectMode: (mode: Mode) => void;
  competitionGroups?: readonly ArenaPickers_so5LeaderboardGroup[];
  selectedLeaderboard?: string;
  onSelectLeaderboard?: (slug: string) => void;
};

export const ArenaPickers = ({
  rarities,
  selectedRarity,
  onSelectRarity,
  modes,
  selectedMode,
  onSelectMode,
  competitionGroups,
  selectedLeaderboard,
  onSelectLeaderboard,
}: Props) => {
  return (
    <Centered
      style={{
        alignItems: 'flex-end',
        gap: 'var(--half-unit)',
      }}
      left={
        <RarityPicker
          rarities={rarities}
          selected={selectedRarity}
          onSelect={onSelectRarity}
          size="sm"
        />
      }
      right={
        !!competitionGroups && !!onSelectLeaderboard ? (
          <CompetitionPicker
            groups={competitionGroups}
            selected={selectedLeaderboard}
            onSelect={onSelectLeaderboard}
            size="sm"
          />
        ) : undefined
      }
    >
      <ModePicker
        modes={modes}
        selected={selectedMode}
        onSelect={onSelectMode}
      />
    </Centered>
  );
};

ArenaPickers.fragments = {
  so5LeaderboardGroup: gql`
    fragment ArenaPickers_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      ...CompetitionPicker_so5LeaderboardGroup
    }
    ${CompetitionPicker.fragments.so5LeaderboardGroup}
  ` as TypedDocumentNode<ArenaPickers_so5LeaderboardGroup>,
};
